import React, { useEffect } from 'react';
import { countryCodes } from '../data/countryCodes';
import type { BrevoFormConfig } from '../data/brevoForms';
interface BrevoResultFormProps {
  config: BrevoFormConfig;
  /** Se dispara cuando Brevo confirma el registro (aparece su panel de éxito). */
  onSuccess?: () => void;
}
const labelStyle: React.CSSProperties = {
  fontWeight: 700,
  textAlign: 'left',
  fontFamily: 'Helvetica, sans-serif',
  fontSize: '16px',
  color: '#3c4858'
};
const errorStyle: React.CSSProperties = {
  fontFamily: 'Helvetica, sans-serif',
  fontSize: '16px',
  textAlign: 'left',
  color: '#661d1d',
  backgroundColor: '#ffeded',
  borderColor: '#ff4949',
  borderRadius: '3px'
};
const specStyle: React.CSSProperties = {
  fontFamily: 'Helvetica, sans-serif',
  fontSize: '12px',
  textAlign: 'left',
  color: '#8390A4'
};
const panelBaseStyle: React.CSSProperties = {
  fontFamily: 'Helvetica, sans-serif',
  fontSize: '16px',
  textAlign: 'left',
  borderRadius: '3px',
  maxWidth: '540px'
};
const SCRIPT_ID = 'brevo-sib-forms-script';
const SCRIPT_SRC = 'https://sibforms.com/forms/end-form/build/main.js';
const INVALID_MESSAGE = 'Ese correo no parece estar completo. Revísalo e inténtalo de nuevo, quiero asegurarme de que tu resultado te llegue.';
const REQUIRED_MESSAGE = 'Este campo es necesario para poder enviarte tu resultado. Complétalo para continuar';

/**
 * Carga el script oficial de formularios de Brevo. Valida y envía por AJAX,
 * así el visitante se queda en la landing y ve el mensaje de éxito aquí mismo.
 * El script se reinyecta cuando cambia el formulario para que se enlace al DOM actual.
 */
function useBrevoFormScript(dependency: string) {
  useEffect(() => {
    const w = window as unknown as Record<string, unknown>;
    w.REQUIRED_CODE_ERROR_MESSAGE = 'Elige un código de país';
    w.LOCALE = 'es';
    w.EMAIL_INVALID_MESSAGE = INVALID_MESSAGE;
    w.SMS_INVALID_MESSAGE = INVALID_MESSAGE;
    w.REQUIRED_ERROR_MESSAGE = REQUIRED_MESSAGE;
    w.GENERIC_INVALID_MESSAGE = INVALID_MESSAGE;
    w.INVALID_NUMBER = INVALID_MESSAGE;
    w.INVALID_DATE = 'Introduce una fecha válida';
    w.REQUIRED_MULTISELECT_MESSAGE = 'Selecciona al menos una opción';
    w.translation = {
      common: {
        selectedList: '{quantity} lista seleccionada',
        selectedLists: '{quantity} listas seleccionadas',
        selectedOption: '{quantity} seleccionado',
        selectedOptions: '{quantity} seleccionados'
      }
    };
    w.AUTOHIDE = false;
    document.getElementById(SCRIPT_ID)?.remove();
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.defer = true;
    document.body.appendChild(script);
    return () => {
      script.remove();
    };
  }, [dependency]);
}

/** Observa el panel de éxito de Brevo para saber cuándo se registró el contacto. */
function useBrevoSuccess(onSuccess?: () => void) {
  useEffect(() => {
    if (!onSuccess) return;
    const panel = document.getElementById('success-message');
    if (!panel) return;
    function isPanelVisible() {
      return Boolean(panel) && window.getComputedStyle(panel as Element).display !== 'none';
    }
    function check() {
      if (isPanelVisible()) onSuccess?.();
    }
    const observer = new MutationObserver(check);
    observer.observe(panel, {
      attributes: true,
      attributeFilter: ['style', 'class']
    });
    // Respaldo: algunos navegadores aplican el cambio sin mutar atributos observables.
    const poll = window.setInterval(check, 500);
    return () => {
      observer.disconnect();
      window.clearInterval(poll);
    };
  }, [onSuccess]);
}
export function BrevoResultForm({
  config,
  onSuccess
}: BrevoResultFormProps) {
  useBrevoFormScript(config.action);
  useBrevoSuccess(onSuccess);
  const phoneAttr = config.phoneAttribute;
  const phoneLabel = phoneAttr === 'WHATSAPP' ? 'WHATSAPP' : 'SMS';

  /**
   * Respaldo del aviso de registro: si los datos son válidos, el contacto ya salió
   * hacia Brevo, así que avisamos aunque su panel de éxito no llegue a mostrarse.
   */
  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    if (!onSuccess) return;
    if (!event.currentTarget.checkValidity()) return;
    window.setTimeout(() => onSuccess(), 1400);
  }
  return <div className="sib-form" style={{
    textAlign: 'center',
    backgroundColor: 'transparent'
  }} data-id="element-6" data-mp="1tgup3s2r4uyb">
      <div id="sib-form-container" className="sib-form-container" data-id="element-7" data-mp="1yfpfbf31tlsg">
        <div id="error-message" className="sib-form-message-panel" style={{
        ...panelBaseStyle,
        color: '#661d1d',
        backgroundColor: '#ffeded',
        borderColor: '#ff4949'
      }} data-id="element-8" data-mp="2m8o3ddz936cv">
          <div className="sib-form-message-panel__text sib-form-message-panel__text--center" data-id="element-9" data-mp="2h7qnj81pqoej">
            <svg viewBox="0 0 512 512" className="sib-icon sib-notification__icon" aria-hidden="true" data-id="element-10" data-mp="11r01q0ygffca">
              <path d="M256 40c118.621 0 216 96.075 216 216 0 119.291-96.61 216-216 216-119.244 0-216-96.562-216-216 0-119.203 96.602-216 216-216m0-32C119.043 8 8 119.083 8 256c0 136.997 111.043 248 248 248s248-111.003 248-248C504 119.083 392.957 8 256 8zm-11.49 120h22.979c6.823 0 12.274 5.682 11.99 12.5l-7 168c-.268 6.428-5.556 11.5-11.99 11.5h-8.979c-6.433 0-11.722-5.073-11.99-11.5l-7-168c-.283-6.818 5.167-12.5 11.99-12.5zM256 340c-15.464 0-28 12.536-28 28s12.536 28 28 28 28-12.536 28-28-12.536-28-28-28z" data-id="element-11" data-mp="15tmmxj0me20i" />
            </svg>
            <span className="sib-form-message-panel__inner-text" data-id="element-12" data-mp="fjm64trosmzp">{REQUIRED_MESSAGE}</span>
          </div>
        </div>

        <div data-id="element-13" data-mp="ahwsr7gzrx8g" />

        <div id="success-message" className="sib-form-message-panel" style={{
        ...panelBaseStyle,
        color: '#085229',
        backgroundColor: '#e7faf0',
        borderColor: '#13ce66'
      }} data-id="element-14" data-mp="ai6kp0llkegs">
          <div className="sib-form-message-panel__text sib-form-message-panel__text--center" data-id="element-15" data-mp="3swftvmult79g">
            <svg viewBox="0 0 512 512" className="sib-icon sib-notification__icon" aria-hidden="true" data-id="element-16" data-mp="3nyeat1x6h7xz">
              <path d="M256 8C119.033 8 8 119.033 8 256s111.033 248 248 248 248-111.033 248-248S392.967 8 256 8zm0 464c-118.664 0-216-96.055-216-216 0-118.663 96.055-216 216-216 118.664 0 216 96.055 216 216 0 118.663-96.055 216-216 216zm141.63-274.961L217.15 376.071c-4.705 4.667-12.303 4.637-16.97-.068l-85.878-86.572c-4.667-4.705-4.637-12.303.068-16.97l8.52-8.451c4.705-4.667 12.303-4.637 16.97.068l68.976 69.533 163.441-162.13c4.705-4.667 12.303-4.637 16.97.068l8.451 8.52c4.668 4.705 4.637 12.303-.068 16.97z" data-id="element-17" data-mp="hh5mkphsttfm" />
            </svg>
            <span className="sib-form-message-panel__inner-text" data-id="element-18" data-mp="7jhgx4inn1io">{config.successMessage}</span>
          </div>
        </div>

        <div data-id="element-19" data-mp="cj3wvlpvavv6" />

        <div id="sib-container" className="sib-container--large sib-container--vertical" style={{
        maxWidth: '540px',
        textAlign: 'center',
        backgroundColor: 'rgba(255,255,255,1)',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: '#C0CCD9',
        borderRadius: '3px',
        direction: 'ltr'
      }} data-id="element-20" data-mp="cjlykxvau2ah">
          <form id="sib-form" method="POST" action={config.action} data-type="subscription" onSubmit={handleSubmit} data-id="element-21" data-mp="33hgwsg77h4yd">
            <div style={{
            padding: '8px 0'
          }} data-id="element-22" data-mp="2tqci7hlfzm42">
              <div className="sib-form-block" style={{
              fontFamily: 'Helvetica, sans-serif',
              fontSize: '32px',
              fontWeight: 700,
              textAlign: 'left',
              color: '#3C4858',
              backgroundColor: 'transparent'
            }} data-id="element-23" data-mp="2pp0uxiq1jcz1">
                <p data-id="element-24" data-mp="2aue444p7nog1">
                  {config.title}
                  {config.titleStrong && <strong data-id="element-25" data-mp="dz7zoswvdzuj">{config.titleStrong}</strong>}
                </p>
              </div>
            </div>

            <div style={{
            padding: '8px 0'
          }} data-id="element-26" data-mp="8yk7w50thez9">
              <div className="sib-form-block" style={{
              fontFamily: 'Helvetica, sans-serif',
              fontSize: '16px',
              textAlign: 'left',
              color: '#3C4858',
              backgroundColor: 'transparent'
            }} data-id="element-27" data-mp="3e0f2nyu08ct5">
                <div className="sib-text-form-block" data-id="element-28" data-mp="398f5ge5bjy09">
                  <p data-id="element-29" data-mp="2v9nw151ej0ig">{config.description}</p>
                </div>
              </div>
            </div>

            <div style={{
            padding: '8px 0'
          }} data-id="element-30" data-mp="309tkkccdk6wb">
              <div className="sib-input sib-form-block" data-id="element-31" data-mp="30alfdky5e0lj">
                <div className="form__entry entry_block" data-id="element-32" data-mp="2maaoihwu8m0d">
                  <div className="form__label-row " data-id="element-33" data-mp="2mbvhw47f1ywn">
                    <label className="entry__label" style={labelStyle} htmlFor="NOMBRE" data-required="*" data-id="element-34" data-mp="2qbvurkeva21l">
                      Introduce tu NOMBRE
                    </label>
                    <div className="entry__field" data-id="element-35" data-mp="2910a5uu0w49s">
                      <input className="input " maxLength={200} type="text" id="NOMBRE" name="NOMBRE" autoComplete="off" placeholder="NOMBRE" data-required="true" required data-id="element-36" data-mp="28zfiqe3091g5" />
                    </div>
                  </div>
                  <label className="entry__error entry__error--primary" style={errorStyle} data-id="element-37" data-mp="2npwhtqs63f9v" />
                  <label className="entry__specification" style={specStyle} data-id="element-38" data-mp="31ped8ozyxqs7">
                    ¿Cual es tu nombre?
                  </label>
                </div>
              </div>
            </div>

            <div style={{
            padding: '8px 0'
          }} data-id="element-39" data-mp="17dhjgpe208tz">
              <div className="sib-input sib-form-block" data-id="element-40" data-mp="17e9b5p4j5mez">
                <div className="form__entry entry_block" data-id="element-41" data-mp="12emxjeksi45n">
                  <div className="form__label-row " data-id="element-42" data-mp="47gz2w7c9fkq">
                    <label className="entry__label" style={labelStyle} htmlFor="EMAIL" data-required="*" data-id="element-43" data-mp="490e6jh9dkkx">
                      Introduce tu EMAIL
                    </label>
                    <div className="entry__field" data-id="element-44" data-mp="3men3jrkckx2j">
                      <input className="input " type="text" id="EMAIL" name="EMAIL" autoComplete="off" placeholder="EMAIL" data-required="true" required data-id="element-45" data-mp="my6ww6i1yc6q" />
                    </div>
                  </div>
                  <label className="entry__error entry__error--primary" style={errorStyle} data-id="element-46" data-mp="16mpkwkrbnjju" />
                  <label className="entry__specification" style={specStyle} data-id="element-47" data-mp="11uxqfz51otof">
                    Asegúrate que este bien escrito.
                  </label>
                </div>
              </div>
            </div>

            <div style={{
            padding: '8px 0'
          }} data-id="element-48" data-mp="1e4x6ra1ml3aa">
              <div className="sib-sms-field sib-form-block" data-id="element-49" data-mp="1e7t3ww5nfnxo">
                <div className="form__entry entry_block" data-id="element-50" data-mp="1j3qml94dx6dm">
                  <div className="form__label-row " data-id="element-51" data-mp="1n6eqr3i5lp49">
                    <label className="entry__label" style={labelStyle} htmlFor={phoneAttr} data-required="*" data-id="element-52" data-mp="1n7ybb6auembu">
                      Introduce tu {phoneLabel}
                    </label>
                    <div className="sib-sms-input-wrapper" style={{
                    direction: 'ltr'
                  }} data-id="element-53" data-mp="21wm5b851inzo">
                      <div className="sib-sms-input" data-placeholder={phoneLabel} data-required="true" data-country-code="AR" data-value="" data-attributename={phoneAttr} data-id="element-54" data-mp="26t2pxsd16k80">
                        <div className="entry__field" data-id="element-55" data-mp="fqghcqitrni4">
                          <select className="input" name={`${phoneAttr}__COUNTRY_CODE`} data-required="true" defaultValue={config.defaultCountryCode} aria-label="Prefijo de país" data-id="element-56" data-mp="bs94i6rhf6c7">
                            {countryCodes.map(country => <option key={`${country.code}-${country.iso}`} value={country.code} data-id="element-57" data-mp="uh7ac1w5ksmt">
                                {country.code} {country.iso}
                              </option>)}
                          </select>
                        </div>
                        <div className="entry__field" style={{
                        width: '100%'
                      }} data-id="element-58" data-mp="3a9mr6aveiug2">
                          <input type="tel" className="input" id={phoneAttr} name={phoneAttr} autoComplete="off" placeholder={phoneLabel} data-required="true" required data-id="element-59" data-mp="35ajxfmkvgane" />
                        </div>
                      </div>
                      <div className="sib-sms-tooltip" data-id="element-60" data-mp="121htzxdup9sj">
                        <div className="sib-sms-tooltip__box" data-id="element-61" data-mp="11zwzu8vg7sv7">
                          El campo {phoneLabel} debe contener entre 6 y 19 cifras e incluir el
                          prefijo del país sin «+» ni «0» delante (ej.: 34xxxxxxxxxxx para España)
                        </div>
                        <span className="sib-sms-tooltip__icon" data-id="element-62" data-mp="x2nf36j085dp">?</span>
                      </div>
                    </div>
                  </div>
                  <label className="entry__error entry__error--primary" style={errorStyle} data-id="element-63" data-mp="2rg6aoath5dwd" />
                  <label className="entry__error entry__error--secondary" style={errorStyle} data-id="element-64" data-mp="22pid6jl2i92n" />
                  <label className="entry__specification" style={specStyle} data-id="element-65" data-mp="22n5vm7j820q4">
                    {phoneAttr === 'WHATSAPP' ? 'Indica tu número de WhatsApp para suscribirte. Por ejemplo, +34664734589' : 'Te avisaremos por aquí cuando abramos el webinar en vivo.'}
                  </label>
                </div>
              </div>
            </div>

            <div style={{
            padding: '8px 0'
          }} data-id="element-66" data-mp="1jsh70vfs22x4">
              <div className="sib-form-block" style={{
              textAlign: 'center'
            }} data-id="element-67" data-mp="1ezo50fbp18oq">
                <button className="sib-form-block__button sib-form-block__button-with-loader" style={{
                fontFamily: 'Helvetica, sans-serif',
                fontSize: '21px',
                fontWeight: 700,
                textAlign: 'center',
                color: '#FFFFFF',
                backgroundColor: '#3e5740',
                borderWidth: '0px',
                borderRadius: '10px'
              }} form="sib-form" type="submit" data-id="element-68" data-mp="1a0bglq56btk1">
                  <svg className="icon clickable__icon progress-indicator__icon sib-hide-loader-icon" viewBox="0 0 512 512" aria-hidden="true" data-id="element-69" data-mp="3gp3nglns63qz">
                    <path d="M460.116 373.846l-20.823-12.022c-5.541-3.199-7.54-10.159-4.663-15.874 30.137-59.886 28.343-131.652-5.386-189.946-33.641-58.394-94.896-95.833-161.827-99.676C261.028 55.961 256 50.751 256 44.352V20.309c0-6.904 5.808-12.337 12.703-11.982 83.556 4.306 160.163 50.864 202.11 123.677 42.063 72.696 44.079 162.316 6.031 236.832-3.14 6.148-10.75 8.461-16.728 5.01z" data-id="element-70" data-mp="2o17jqwzsnbkp" />
                  </svg>
                  {config.buttonLabel}
                </button>
              </div>
            </div>

            <input type="text" name="email_address_check" defaultValue="" className="input--hidden" data-id="element-71" data-mp="2a7zs55bpqg30" />
            <input type="hidden" name="locale" value="es" data-id="element-72" data-mp="1y7017gvs2tr7" />
          </form>
        </div>
      </div>
    </div>;
}